import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardLocationFormPayload,
	LOCATION_CSV_LABELS,
	LocationCsvKeys,
	LocationCsvTemplate,
	LOCATION_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { FormUtil } from '../../../../utils/form.util';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

type LocationForm = {
	[LocationCsvKeys.ssoLocationId]: FormControl<string | null>;
	[LocationCsvKeys.name]: FormControl<string | null>;
	[LocationCsvKeys.displayOrder]: FormControl<number | null>;
	[LocationCsvKeys.locationUuid]: FormControl<string | null>;
	[LocationCsvKeys.clientUuid]: FormControl<string | null>;
	[LocationCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-location-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
	],
	standalone: true,
	templateUrl: './setup-wizard-location-form.html',
	styleUrl: './setup-wizard-location-form.scss',
})
export class SetupWizardLocationForm {
	public edit = input<LocationCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardLocationFormPayload>();
	public LocationCsvKeys = LocationCsvKeys;
	public LOCATION_CSV_LABELS = LOCATION_CSV_LABELS;
	public Validators = Validators;
	private clientUuid: string | undefined;
	private sub?: Subscription;

	public form = new FormGroup<LocationForm>({
		[LocationCsvKeys.ssoLocationId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LocationCsvKeys.name]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LocationCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LocationCsvKeys.locationUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LocationCsvKeys.clientUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[LocationCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor(private route: ActivatedRoute) {
		FormUtil.applyCsvSpecValidators(this.form, LOCATION_CSV_SPEC);

		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];

			this.form.patchValue({
				[LocationCsvKeys.clientUuid]: this.clientUuid,
			});
		});
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a locationUuid we can remove all validators for ssoLocationId
			if (edit[LocationCsvKeys.locationUuid]) {
				this.form.get(LocationCsvKeys.ssoLocationId)?.clearValidators();
				this.form.get(LocationCsvKeys.ssoLocationId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[LocationCsvKeys.ssoLocationId]: edit[LocationCsvKeys.ssoLocationId],
				[LocationCsvKeys.name]: edit[LocationCsvKeys.name],
				[LocationCsvKeys.displayOrder]: edit[LocationCsvKeys.displayOrder],
				[LocationCsvKeys.locationUuid]: edit[LocationCsvKeys.locationUuid],
				[LocationCsvKeys.clientUuid]: edit[LocationCsvKeys.clientUuid],
				[LocationCsvKeys.active]: edit[LocationCsvKeys.active],
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[LocationCsvKeys.ssoLocationId]: value[LocationCsvKeys.ssoLocationId] ?? undefined,
				[LocationCsvKeys.name]: value[LocationCsvKeys.name] ?? undefined,
				[LocationCsvKeys.displayOrder]: value[LocationCsvKeys.displayOrder] ?? undefined,
				[LocationCsvKeys.locationUuid]: value[LocationCsvKeys.locationUuid] ?? undefined,
				[LocationCsvKeys.clientUuid]: value[LocationCsvKeys.clientUuid] ?? undefined,
				[LocationCsvKeys.active]: value[LocationCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
